import { ACFFormElement, FormState } from "./Form";
import dynamic from "next/dynamic";
import { FormElement } from "./createFormStructure";
/* jscpd:ignore-start */
import {
  FormErrorState,
  InputV2,
  TextAreaV2,
  SelectV2,
  FieldWrapper,
  LabelV2,
  CheckboxV2,
  RadioV2,
  FileUploadV2,
} from "./Inputs/Inputs";
import { FormData } from "@/getData/getIndexedFormsData";
import React from "react";
import FormBox from "@/modules/Form/box/FormBox.tsx";
import { isFieldVisible } from "./isFieldVisible";
import styles from "./Form.module.scss";

const FriendlyCaptcha = dynamic(
  () => import("../../components/FriendlyCaptcha/FriendlyCaptcha"),
  { ssr: false },
);
export const genFieldName = (fieldLabel: string) => {
  return fieldLabel.replace(/[ äüö.;]/g, "").toLocaleLowerCase();
};

const inputWidthToColumns = (width: ACFFormElement["input_width"]) => {
  switch (width) {
    case "full":
      return "is-12";
    case "half":
      return "is-6";
    case "one-third":
      return "is-4";
    case "two-third":
      return "is-8";
  }
};

interface RenderFieldsProps {
  fields: FormElement[];
  errors: FormErrorState;
  friendlyCaptchaApplicationKey: string | undefined;
  formValues: Record<string, string | boolean | number | string[]>;
  handleCaptchaComplete: (solution: string) => void;
  formState: FormState;
  formData: FormData;
  setFormValues: (
    values: Record<string, string | boolean | number | string[]>,
  ) => void;
  requiredLabel: string;
  fileRefs: React.MutableRefObject<Record<string, File | null>>;
}

export const renderFields = ({
  fields,
  setFormValues,
  errors,
  friendlyCaptchaApplicationKey,
  formValues,
  handleCaptchaComplete,
  formState,
  formData,
  requiredLabel,
  fileRefs,
}: RenderFieldsProps) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
  ) => {
    if (e.target.type === "checkbox") {
      setFormValues({ ...formValues, [e.target.name]: e.target.checked });
    } else if (e.target.type === "number") {
      setFormValues({ ...formValues, [e.target.name]: e.target.valueAsNumber });
    } else if (e.target.type === "radio") {
      if (formValues[e.target.name] === e.target.value) {
        const { [e.target.name]: _, ...updatedFormValues } = formValues;
        setFormValues(updatedFormValues);
      } else {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
      }
    } else {
      setFormValues({ ...formValues, [e.target.name]: e.target.value });
    }
  };

  const elements: JSX.Element[] = [];

  for (const field of fields) {
    if (!isFieldVisible(field, formValues, formData)) continue;

    const elementKey =
      field.name ??
      (field.type === "fieldset" || field.type === "column"
        ? `${field.type}:${field.label ?? ""}`
        : `${field.type}:${field.label ?? ""}:${field.placeholder ?? ""}`);

    const className = `column ${inputWidthToColumns(field.inputWidth)} ${field.wrapperClassName}`;
    let Tag: JSX.Element = <></>;

    const props = {
      placeholder: field.placeholder,
      label: field.label,
      name: field.name,
      required: field.isRequired,
      className,
      onChange: handleChange,
      errors,
      "aria-label": field.ariaLabel,
      disabled: formState === "loading",
      required_label: requiredLabel,
    };

    const value = formValues[props.name];
    const nonBooleanValue =
      typeof value !== "boolean" ? (value ?? "") : undefined;
    const booleanValue =
      typeof value === "boolean" ? (value ?? false) : undefined;

    switch (field.type) {
      case "column": {
        const children = renderFields({
          fields: field.children || [],
          setFormValues,
          errors,
          friendlyCaptchaApplicationKey,
          formValues,
          handleCaptchaComplete,
          formState,
          formData,
          requiredLabel,
          fileRefs,
        });
        if (children.length > 0) {
          Tag = (
            <div className="column is-12-tablet is-6-desktop">
              <div className="columns is-multiline">{children}</div>
            </div>
          );
        }
        break;
      }
      case "fieldset": {
        const children = renderFields({
          fields: field.children || [],
          setFormValues,
          errors,
          friendlyCaptchaApplicationKey,
          formValues,
          handleCaptchaComplete,
          formState,
          formData,
          requiredLabel,
          fileRefs,
        });

        if (children.length > 0) {
          Tag = (
            <fieldset className="column mb-4 is-12">
              {field.label && (
                <legend className={field.style ?? "h3"}>{field.label}</legend>
              )}
              <div className="columns is-multiline">{children}</div>
            </fieldset>
          );
        }
        break;
      }
      case "infotext":
        if (field.infoTextOptions !== "send-only") {
          Tag = (
            <div
              className={className}
              dangerouslySetInnerHTML={{ __html: field.infoText }}
            />
          );
        }
        break;
      case "text":
      case "email":
      case "tel":
      case "url":
        Tag = (
          <InputV2 inputType={field.type} {...props} value={nonBooleanValue} />
        );
        break;
      case "datepicker":
        Tag = (
          <InputV2
            inputType={"date"}
            {...props}
            min={field.min}
            max={field.max}
            value={nonBooleanValue}
          />
        );
        break;
      case "number":
        Tag = (
          <InputV2
            inputType={field.type}
            {...props}
            min={field.min}
            max={field.max}
            step={field.step}
            value={nonBooleanValue}
          />
        );
        break;
      case "textarea":
        Tag = <TextAreaV2 {...props} rows={field.rows} />;
        break;
      case "dropwdown":
        if (!field.multiSelect) {
          const { onChange: _, ...selectProps } = props;
          const normalizedItems = field.items.map((item) => ({
            label: item.label,
            value: item.label,
          }));
          Tag = (
            <SelectV2
              {...selectProps}
              items={normalizedItems}
              setSelected={(val) => {
                setFormValues({ ...formValues, [props.name]: val });
              }}
              selected={formValues[props.name] as string}
            />
          );
        } else {
          Tag = (
            <FieldWrapper
              errors={errors}
              name={props.name}
              className={className}
            >
              <LabelV2
                Tag="p"
                label={field.label}
                required={field.isRequired}
                required_label={requiredLabel}
              />
              <div className={styles["input-wrapper"]}>
                <div
                  className="columns is-multiline"
                  style={{ "--bulma-column-gap": "4px" } as React.CSSProperties}
                >
                  {field.items.map((item, index) => (
                    <CheckboxV2
                      className="column is-6-tablet"
                      inputType={"checkbox"}
                      key={index}
                      label_checkbox={item.label}
                      onChange={(e) => {
                        const prev = (formValues[props.name] || []) as string[];
                        setFormValues({
                          ...formValues,
                          [props.name]: e.target.checked
                            ? [...prev, index.toString()]
                            : prev.filter((x) => x !== index.toString()),
                        });
                      }}
                    />
                  ))}
                </div>
              </div>
            </FieldWrapper>
          );
        }

        break;
      case "checkbox":
        Tag = (
          <CheckboxV2
            inputType="checkbox"
            label_checkbox={field.labelCheckbox}
            {...props}
            checked={booleanValue}
          />
        );
        break;
      case "radiobutton":
        Tag = (
          <RadioV2
            options={field.options.map((o, key) => ({
              ...o,
              checked:
                key === Number(formValues[props.name]) ||
                o.value === formValues[props.name],
            }))}
            {...props}
          />
        );
        break;
      case "file":
        Tag = (
          <FileUploadV2
            {...props}
            labelUploadButton={field.labelUploadButton}
            ariaLabelUploadButton={field.ariaLabelUploadButton}
            imageUploadButton={field.imageUploadButton}
            labelRemoveButton={field.labelRemoveButton}
            ariaLabelRemoveButton={field.ariaLabelRemoveButton}
            imageRemoveButton={field.imageRemoveButton}
            file={fileRefs.current[props.name] ?? null}
            onFileChange={(file: File | null) => {
              // 1. Das File-Objekt weiterhin im Ref speichern (für direkten Zugriff)
              fileRefs.current[props.name] = file;

              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  // 2. Den Base64-String in den Form-State schreiben
                  const base64String = reader.result as string;
                  setFormValues({
                    ...formValues,
                    [props.name]: base64String,
                  });
                };
                reader.readAsDataURL(file); // Konvertierung starten
              } else {
                // 3. Wenn die Datei entfernt wird, State leeren
                setFormValues({
                  ...formValues,
                  [props.name]: "",
                });
              }
            }}
          />
        );
        break;
      case "box":
        Tag = (
          <FormBox
            className={props.className}
            headline={field.box_headline}
            image={field.box_image}
            text={field.box_text}
          />
        );
        break;
      case "captcha":
        if (friendlyCaptchaApplicationKey) {
          const hasError = errors && props.name && errors[props.name];
          Tag = (
            <div
              data-field-name={props.name}
              className={`${hasError ? "has-error" : ""} ${props.className}`}
            >
              <FriendlyCaptcha
                onComplete={handleCaptchaComplete}
                sitekey={friendlyCaptchaApplicationKey}
              />
              {hasError && (
                <p className={`error`}>{hasError && errors[props.name!]}</p>
              )}
            </div>
          );
        }
    }

    if (Tag.type !== React.Fragment || (Tag.props && Tag.props.children)) {
      elements.push(<React.Fragment key={elementKey}>{Tag}</React.Fragment>);
    }
  }
  return elements;
};
/* jscpd:ignore-end */
